import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  FaGraduationCap,
  FaUserFriends,
  FaClock,
  FaBriefcase,
  FaShieldAlt,
  FaClipboardList,
  FaFileInvoiceDollar,
  FaQuestionCircle,
  FaExternalLinkAlt,
  FaArrowRight,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";

/*
  Student Visa (Subclass 500) — modern, stylish, and informative
  - Gradient hero with tilted feature stats
  - Sticky quick-nav (desktop)
  - Tilted feature cards
  - Live eligibility readiness checker
  - Step-by-step timeline
  - Rich FAQs (accessible accordion)
  - Strong CTA ribbon
*/

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06 } }),
};

const FEATURES = [
  { icon: <FaGraduationCap />, title: "CRICOS study", body: "Study full‑time at an approved Australian provider." },
  { icon: <FaUserFriends />, title: "Bring family", body: "Eligible dependants can accompany you to Australia." },
  { icon: <FaClock />, title: "Stay aligned", body: "Visa length usually aligns with your course duration." },
  { icon: <FaBriefcase />, title: "Work rights", body: "48 hrs/fortnight in session; unlimited in official breaks." },
];

const FAQ = [
  { q: "Can I work full‑time while studying?", a: "No — while your course is in session, work is generally limited to 48 hours per fortnight. During official breaks you can usually work unlimited hours." },
  { q: "Can my partner work?", a: "If you study a Masters or PhD, your eligible partner can usually work unlimited hours. Otherwise, partners are typically limited to 48 hours per fortnight." },
  { q: "Do I need OSHC?", a: "Yes. You must maintain Overseas Student Health Cover (OSHC) for the entire visa length unless exempt." },
  { q: "What if I change courses?", a: "You must maintain a valid CoE and meet Genuine Student, attendance and progress requirements. Changing to a lower AQF level can affect your visa — get advice first." },
];

export default function StudentVisa500() {
  useEffect(() => { document.title = "Student Visa (Subclass 500) | Glide Education"; }, []);

  const [hasCOE, setHasCOE] = useState(false);
  const [hasOSHC, setHasOSHC] = useState(false);
  const [hasFunds, setHasFunds] = useState(false);
  const [hasEnglish, setHasEnglish] = useState(false);
  const ready = useMemo(() => hasCOE && hasOSHC && hasFunds && hasEnglish, [hasCOE, hasOSHC, hasFunds, hasEnglish]);

  return (
    <motion.main initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="min-h-screen bg-white">
      {/* HERO */}
      <div className="relative overflow-hidden bg-gradient-to-b from-blue-50 to-white">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-6xl px-6 pt-12 pb-10 md:pb-14">
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" className="text-3xl md:text-5xl font-extrabold tracking-tight text-blue-900">
            Student Visa — Subclass 500
          </motion.h1>
          <motion.p variants={fadeUp} custom={1} initial="hidden" animate="show" className="mt-3 max-w-3xl text-gray-700">
            Study full‑time in Australia at a registered education provider, bring eligible family members, and enjoy
            limited work rights while you study.
          </motion.p>

          {/* Quick stats — Tilted chips */}
          <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => (
              <Tilt key={f.title} tiltMaxAngleX={8} tiltMaxAngleY={8} scale={1.02} transitionSpeed={400} glareEnable glareMaxOpacity={0.12} glareColor="#60a5fa" className="rounded-2xl border bg-white/80 p-4 shadow-sm backdrop-blur">
                <div className="flex items-center gap-3 text-blue-900">
                  <span className="text-xl">{f.icon}</span>
                  <div className="font-semibold">{f.title}</div>
                </div>
                <p className="mt-1 text-sm text-gray-700">{f.body}</p>
              </Tilt>
            ))}
          </motion.div>

          {/* Primary CTAs */}
          <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-6 flex flex-wrap gap-3">
            <a
              href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white shadow hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Official Home Affairs page <FaExternalLinkAlt className="opacity-90" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-xl border border-blue-600 bg-white px-5 py-2.5 font-semibold text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              Book a Free Consultation <FaArrowRight />
            </Link>
          </motion.div>

          <Note className="mt-6">
            <strong>Current fee:</strong> The Student visa application charge is <strong>AUD $2,000</strong> for primary applicants
            from <strong>1 July 2025</strong>. Some exemptions apply. (*Work‑hours limit is generally <strong>48 hours per fortnight</strong>
            during study; unlimited in official breaks. Research Masters/PhD have different settings.)
          </Note>
        </div>
      </div>

      {/* BODY GRID */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-6 pb-16 md:grid-cols-[1fr_320px]">
        {/* LEFT */}
        <div>
          {/* Who */}
          <Section title="Who is this visa for?">
            International students who want to study full‑time at a CRICOS‑registered institution — schools, ELICOS, VET, higher education, postgraduate research, non‑award, or packaged courses.
          </Section>

          {/* Eligibility */}
          <Section title="Key eligibility">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Genuine Student (GS)</strong> requirement — your primary purpose is study in Australia with a clear pathway.</li>
              <li><strong>Confirmation of Enrolment (CoE)</strong> for each course.</li>
              <li><strong>English language</strong> evidence per current rules.</li>
              <li><strong>Financial capacity</strong> to cover tuition, travel and living costs (you and any dependants).</li>
              <li><strong>OSHC</strong> for the full stay (unless exempt).</li>
              <li><strong>Health & character</strong> checks as requested.</li>
            </ul>
            <Note>From <strong>7 Aug 2025</strong>, English settings changed. Check the latest accepted tests and scores on Home Affairs.</Note>
          </Section>

          {/* Work rights */}
          <Section title="Work rights">
            <ul className="list-disc pl-5 space-y-2">
              <li>Up to <strong>48 hours per fortnight</strong> while your course is in session; <strong>unlimited</strong> in official breaks.</li>
              <li>Dependants of <strong>Masters/PhD</strong> students generally have <strong>unlimited</strong> work rights; otherwise typically 48 hours/fortnight.</li>
              <li>Breaches can lead to visa cancellation — keep time records and stay compliant.</li>
            </ul>
          </Section>

          {/* Costs */}
          <Section title="Costs & processing time">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Application charge:</strong> AUD $2,000 (primary applicant) from 1 July 2025 (plus additional applicants & other costs). Some concessions for Pacific & Timor‑Leste citizens.</li>
              <li><strong>Processing times:</strong> updated by Home Affairs. Times vary by sector and case quality.</li>
            </ul>
            <div className="mt-3 flex gap-3">
              <a className="text-blue-700 underline" href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-processing-times" target="_blank" rel="noreferrer">Check current processing times</a>
              <a className="text-blue-700 underline" href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500" target="_blank" rel="noreferrer">Pricing & eligibility (official)</a>
            </div>
          </Section>

          {/* Readiness checker */}
          <Section title="Quick readiness check">
            <div className="grid gap-4 md:grid-cols-2">
              <Checklist id="chk-coe" checked={hasCOE} onChange={setHasCOE} label="I have a valid CoE (or offer) for my course" icon={<FaClipboardList />} />
              <Checklist id="chk-oshc" checked={hasOSHC} onChange={setHasOSHC} label="I will maintain OSHC for my entire stay" icon={<FaShieldAlt />} />
              <Checklist id="chk-funds" checked={hasFunds} onChange={setHasFunds} label="I can show sufficient funds for tuition, travel & living" icon={<FaFileInvoiceDollar />} />
              <Checklist id="chk-english" checked={hasEnglish} onChange={setHasEnglish} label="I meet the current English requirement (or exemption)" icon={<FaClipboardList />} />
            </div>
            <div className={`mt-4 rounded-lg px-4 py-3 text-sm ${ready ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-800"}`}>
              {ready ? (
                <span className="inline-flex items-center gap-2 font-medium"><FaCheckCircle /> Looks good! You're broadly ready to prepare your Subclass 500 application.</span>
              ) : (
                <span className="inline-flex items-center gap-2"><FaInfoCircle className="text-blue-600" /> Not sure yet? We’ll review your course, funds, OSHC and English to confirm the best approach.</span>
              )}
            </div>
          </Section>

          {/* Step-by-step timeline */}
          <Section title="Step‑by‑step: how to apply">
            <div className="grid gap-4 md:grid-cols-3">
              <Step icon={<FaClipboardList />} title="Offer & CoE" body="Accept your offer and obtain a Confirmation of Enrolment for each course." />
              <Step icon={<FaShieldAlt />} title="OSHC & docs" body="Arrange OSHC; gather English, funds, identity, and health evidence." />
              <Step icon={<FaClock />} title="Lodge & follow up" body="Complete ImmiAccount form, pay, and respond to any requests quickly." />
            </div>
          </Section>

          {/* FAQs */}
          <Section title="FAQs">
            <div className="space-y-3">
              {FAQ.map((f) => (
                <Accordion key={f.q} title={f.q} body={f.a} />
              ))}
            </div>
          </Section>

          {/* CTA ribbon */}
          <section className="mt-12">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]">
              <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
                <div className="grid items-center gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="text-2xl font-bold text-blue-900">Start your study journey the smart way.</h3>
                    <p className="mt-1 text-gray-600">We’ll review your eligibility, prepare a compliant application, and guide OSHC, funds and GS evidence.</p>
                  </div>
                  <div className="flex md:justify-end">
                    <Link to="/contact" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500">
                      Book a Free Consultation <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </section>
        </div>

        {/* RIGHT: sticky quick nav */}
        <aside className="top-24 hidden md:block">
          <div className="sticky top-24 rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
            <div className="text-sm font-semibold text-blue-900">Quick links</div>
            <nav className="mt-3">
              <ul className="space-y-2 text-sm">
                <li><a className="text-blue-700 hover:underline" href="#">Top</a></li>
                <li><a className="text-blue-700 hover:underline" href="#eligibility">Eligibility</a></li>
                <li><a className="text-blue-700 hover:underline" href="#work-rights">Work rights</a></li>
                <li><a className="text-blue-700 hover:underline" href="#costs">Costs & processing</a></li>
                <li><a className="text-blue-700 hover:underline" href="#faq">FAQs</a></li>
              </ul>
            </nav>
            <hr className="my-4 border-blue-100" />
            <a href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/student-500" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-blue-800 hover:bg-blue-100">
              Official guidance <FaExternalLinkAlt className="opacity-80" />
            </a>
          </div>
        </aside>
      </div>
    </motion.main>
  );
}

/* ---------- Bits ---------- */
function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="mb-3 text-2xl font-bold text-blue-700">{title}</h2>
      <div className="leading-relaxed text-gray-700">{children}</div>
    </section>
  );
}

function Note({ children, className = "" }) {
  return (
    <div className={`my-4 rounded-md border-l-4 border-blue-600 bg-blue-50 p-4 text-sm ${className}`}>{children}</div>
  );
}

function Checklist({ id, checked, onChange, icon, label }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-3 rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-400" />
      <span className="flex items-start gap-2 text-sm text-gray-800">
        <span className="mt-0.5 text-blue-600">{icon}</span>
        <span>{label}</span>
      </span>
    </label>
  );
}

function Step({ icon, title, body }) {
  return (
    <Tilt tiltMaxAngleX={8} tiltMaxAngleY={8} scale={1.02} transitionSpeed={400} glareEnable glareMaxOpacity={0.12} glareColor="#60a5fa" className="rounded-2xl border bg-white/80 p-5 shadow-sm backdrop-blur">
      <div className="flex items-center gap-3 text-blue-900">
        <span className="text-lg">{icon}</span>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="mt-1 text-sm text-gray-700">{body}</p>
    </Tilt>
  );
}

function Accordion({ title, body }) {
  const [open, setOpen] = useState(false);
  const id = useMemo(() => `acc-${title.toLowerCase().replace(/\s+/g, "-")}`,[title]);

  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-lg px-1"
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        id={id}
      >
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700" aria-hidden="true">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={`${id}-panel`}
            role="region"
            aria-labelledby={id}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p className="mt-2 text-sm leading-relaxed text-gray-700">{body}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
